import { cartStore } from "@gob/cart-store";
import { CartContentUpdateEvent } from "@gob/events";

const useFetch = async function (endpoint, type = "POST", data) {
  try {
    const res = await fetch(endpoint, {
      method: type,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    }).then((r) => r.json());
    return res;
  } catch (error) {
    throw error;
  }
};

class ComponentProductForm extends HTMLElement {
  #sellingPlanType = 'subscribe';
  #sellingPlanLabel = 'Subscribe Now';
  #buyNowLabel = 'Buy Now';

  constructor() {
    super();
  }

  connectedCallback() {
    this.form = this.querySelector('form');
    this.variantIdInput = this.querySelector('[data-variant-id]');
    this.variantsSelectors = this.querySelector('[data-variant-selectors]');
    this.optionSelectors = this.querySelectorAll('[data-option-selector]');
    this.priceElement = this.querySelector('[data-price]');
    this.addToCartButton = this.querySelector('[data-add-to-cart]');
    this.sellingPlanRadios = this.querySelectorAll('[data-selling-plan-radio]');

    this.hasVariants = this.addToCartButton?.dataset?.hasVariants === 'true';
    this.productData = JSON.parse(this.querySelector('[data-product-json]').textContent);
    this.currentVariant = this.productData.variants?.[0];

    this.variantsExpanded = false;
    this.loading = false;

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.optionSelectors?.forEach(selector => {
      const radioInputs = selector.querySelectorAll('[data-option-value]');
      radioInputs.forEach(radio => {
        radio.addEventListener('change', this.onVariantChange.bind(this));
      });
    });

    this.sellingPlanRadios?.forEach(radio => {
      radio.addEventListener('change', this.onSellingPlanChange.bind(this));
    });

    this.addToCartButton?.addEventListener('click', this.onAddToCartButtonClick.bind(this));
    this.form?.addEventListener('submit', this.onSubmitHandler.bind(this));
  }

  onVariantChange() {
    this.updateOptions();
    this.updateVariantInput();
    this.updatePrice();
    this.updateAddToCartButton();
    this.updateURL();
  }

  onSellingPlanChange(event) {
    const label = event.target.dataset?.label ?? 'Buy Now';

    if (label === this.#sellingPlanLabel) {
      this.#sellingPlanType = 'subscribe';
    } else {
      this.#sellingPlanType = 'buy';
    }

    this.addToCartButton.textContent = label;
  }

  onAddToCartButtonClick(event) {
    if (this.hasVariants && this.variantsSelectors?.dataset?.expanded === 'true') {
      this.variantsExpanded = false;
      this.variantsSelectors.dataset.expanded = this.variantsExpanded;
      return;
    }

    if (!this.hasVariants || !this.variantsSelectors || this.variantsExpanded) return;

    event.preventDefault();
    event.stopPropagation();

    this.variantsExpanded = !this.variantsExpanded;
    this.variantsSelectors.dataset.expanded = this.variantsExpanded;
  }

  updateOptions() {
    const selectedOptions = this.getSelectedOptions();
    this.currentVariant = this.getVariantFromOptions(selectedOptions);
  }

  getSelectedOptions() {
    return Array.from(this.optionSelectors).map(selector => {
      const checkedRadio = selector.querySelector('[data-option-value]:checked');
      return {
        position: selector.dataset.optionPosition,
        value: checkedRadio ? checkedRadio.value : ''
      };
    });
  }

  getVariantFromOptions(options) {
    return this.productData.variants.find(variant => {
      return options.every(option => {
        return variant.options[option.position - 1] === option.value;
      });
    });
  }

  updateVariantInput() {
    if (this.currentVariant) {
      this.variantIdInput.value = this.currentVariant.id;
    }
  }

  updatePrice() {
    if (this.currentVariant && this.priceElement) {
      const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
      const formattedPrice = formatter.format(this.currentVariant.price / 100);
      this.priceElement.textContent = formattedPrice?.replaceAll('$', '');
    }
  }

  updateAddToCartButton() {
    this.addToCartButton.dataset.loading = false;

    if (!this.currentVariant) {
      this.addToCartButton.textContent = 'Sold out,';
      this.addToCartButton.disabled = true;
      return;
    }

    if (this.currentVariant.available) {
      if (this.#sellingPlanType === 'subscribe') {
        this.addToCartButton.textContent = this.#sellingPlanLabel;
      } else {
        this.addToCartButton.textContent = this.#buyNowLabel;
      }
      this.addToCartButton.disabled = false;
    } else {
      this.addToCartButton.textContent = 'Sold out,';
      this.addToCartButton.disabled = true;
    }
  }

  updateURL() {
    if (!this.currentVariant) return;

    const url = new URL(window.location);
    url.searchParams.set('variant', this.currentVariant.id);
    window.history.replaceState({}, '', url);
  }

  async onSubmitHandler(event) {
    event.preventDefault();

    this.lastButtonText = 'Buy Now';

    const activeSellingType = this.form.querySelector('input[name="selling_plan"]:checked');

    if (activeSellingType && activeSellingType.value !== 'onetime') {
      this.lastButtonText = 'Subscribe Now';
    }

    if (this.loading || !this.currentVariant || !this.currentVariant.available) {
      return;
    }

    this.loading = true;

    this.addToCartButton.disabled = true;
    this.addToCartButton.dataset.loading = true;
    if (this.#sellingPlanType === 'subscribe') {
      this.addToCartButton.textContent = 'Subscribing'
    } else {
      this.addToCartButton.textContent = 'Adding to cart';
    }

    try {
      const formData = new FormData(this.form);
      const variantId = formData.get('id');
      const quantity = 1;
      const sellingPlan = formData.get('selling_plan');

      const response = await useFetch('/cart/add.js', 'POST', {
        items: [{
          id: variantId,
          quantity: quantity,
          ...(sellingPlan && sellingPlan !== 'onetime' ? { selling_plan: parseInt(sellingPlan) } : {})
        }],
        sections: 'cart'
      });

      const cartSection = response?.sections?.['cart']

      if (cartSection) {
        const parser = new DOMParser()
        const doc = parser.parseFromString(cartSection, 'text/html')
        const cartSectionContent = doc.querySelector('.cart-drawer__content')
        document.dispatchEvent(new CartContentUpdateEvent(cartSectionContent.innerHTML))
      }

      if (this.#sellingPlanType === 'subscribe') {
        this.addToCartButton.textContent = 'Subscribed';
      } else {
        this.addToCartButton.textContent = 'Added to cart';
      }

      setTimeout(() => {
        this.updateAddToCartButton();
      }, 2000);

      this.dispatchEvent(new CustomEvent('variant:added', {
        bubbles: true,
        detail: {
          variant: this.currentVariant,
          quantity: quantity
        }
      }));

      cartStore.loadCart();

    } catch (error) {
      console.error('Error adding to cart:', error);
      this.addToCartButton.textContent = 'Error - try again,';

      setTimeout(() => {
        this.updateAddToCartButton();
      }, 2000);
    } finally {
      this.loading = false;
    }
  }
}

if (!customElements.get('product-form')) {
  customElements.define('product-form', ComponentProductForm);
}